"use client";
import { useState } from "react";
import { PDFDocument, rgb } from "pdf-lib";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { formatCurrency } from "../../../utils/formatCurrency";
import { capitalize } from "../plan-de-pagos/capitalize";
import { planPagos } from "../../../utils/planPagos";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../FirebaseConfig.ts";
import { formatCurrencyPDF } from "../../../utils/formatCurrencyPDF.js";

dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);
dayjs.locale("es");

function GenerateReportCompany({ name, nit, companyIdentity }) {
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));

  const generarCertificado = async () => {
    setLoading(true);

    try {
      const hostname = window.location.hostname;

      const requestsCollection = collection(db, "requests");
      const companyId =
        hostname === "creditos.sofia.com.co"
          ? "tPxAubnMGdjLnSRk2njL"
          : companyIdentity;

      const q = query(requestsCollection, where("companyId", "==", companyId));
      const data = await getDocs(q);
      const dataA = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

      const dataArray2 = dataA.filter(
        (el) => el.status === "pagado" || el.status === "aprobado"
      );

      const fechaSeleccionada = dayjs(date, "YYYY-MM-DD").startOf("day");

      const dataArray = dataArray2.filter((el) => {
        if (!el.fechaPagoCompleto) return true;
        const fechaPagoCompleto = dayjs(
          el.fechaPagoCompleto,
          "YYYY-MM-DD"
        ).startOf("day");
        return fechaPagoCompleto.isAfter(fechaSeleccionada);
      });

      // Crear PDF desde cero
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]); // A4
      const { width, height } = page.getSize();

      const font = await pdfDoc.embedFont("Helvetica");
      const fontBold = await pdfDoc.embedFont("Helvetica-Bold");

      // ======= Logo =======
      const logoBytes = await fetch("/logo_lendy.png").then((res) =>
        res.arrayBuffer()
      );
      const logoImage = await pdfDoc.embedPng(logoBytes);
      const logoDims = logoImage.scale(0.1);
      page.drawImage(logoImage, {
        x: 40,
        y: height - 60,
        width: logoDims.width,
        height: logoDims.height,
      });

      // ======= Título =======
      page.drawText("Estado de Cuenta de créditos", {
        x: 370,
        y: height - 60,
        size: 13,
        font: fontBold,
      });

      // ======= Caja información de la empresa =======
      const infoTop = height - 100;
      const boxHeight = 45;
      const boxWidth = width - 80;

      page.drawRectangle({
        x: 40,
        y: infoTop - boxHeight,
        width: boxWidth,
        height: boxHeight,
        color: rgb(0.97, 0.97, 0.97),
        borderColor: rgb(0.75, 0.75, 0.75),
        borderWidth: 0.5,
      });

      page.drawText("Empresa:", {
        x: 50,
        y: infoTop - 15,
        size: 9,
        font: fontBold,
      });
      page.drawText(`${name}`, { x: 110, y: infoTop - 15, size: 9, font });

      page.drawText("NIT:", {
        x: 50,
        y: infoTop - 35,
        size: 9,
        font: fontBold,
      });
      page.drawText(`${nit}`, { x: 75, y: infoTop - 35, size: 9, font });

      page.drawText("Fecha de corte:", {
        x: 330,
        y: infoTop - 15,
        size: 9,
        font: fontBold,
      });
      page.drawText(
        `${dayjs(date).format("YYYY")}/${dayjs(date).format("MM")}/30`,
        {
          x: 420,
          y: infoTop - 15,
          size: 9,
          font,
        }
      );

      page.drawText("Fecha límite de pago:", {
        x: 330,
        y: infoTop - 35,
        size: 9,
        font: fontBold,
      });
      page.drawText(`${dayjs(date).add(1, "month").format("YYYY/MM")}/10`, {
        x: 440,
        y: infoTop - 35,
        size: 9,
        font,
      });

      // ======= Tabla =======
      const tableTop = infoTop - boxHeight - 40;
      const colWidths = [60, 110, 55, 50, 70, 35, 40, 60, 70];
      const headers = [
        "Cédula",
        "Nombre",
        "No. Crédito",
        "Tasa",
        "Monto",
        "Cuotas",
        "Pagos",
        "Valor Cuota",
      ];

      let currentY = tableTop;
      const rowHeight = 18;
      const tableLeft = 40;
      const tableWidth = colWidths.reduce((a, b) => a + b, 0);

      // ======= Encabezado tabla =======
      page.drawRectangle({
        x: tableLeft,
        y: currentY - rowHeight,
        width: tableWidth - 60,
        height: rowHeight,
        color: rgb(0.93, 0.93, 0.93),
        borderColor: rgb(0.7, 0.7, 0.7),
        borderWidth: 0.5,
      });

      let x = tableLeft + 4;
      headers.forEach((header, i) => {
        page.drawText(header, {
          x,
          y: currentY - 12,
          size: 7.5,
          font: fontBold,
        });
        x += colWidths[i];
      });

      currentY -= rowHeight;

      // ======= Filas =======
      let totalCuotas = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const el = dataArray[i];
        const { schedule } = planPagos(el);

        const cuotasFiltradas = schedule.filter((s) => {
          const [day, month, year] = s.fechaPago.split("/");
          const fechaCuota = dayjs(`${year}-${month}-30`);
          return (
            fechaCuota.month() === dayjs(date).month() &&
            fechaCuota.year() === dayjs(date).year()
          );
        });

        const pagosHastaFecha =
          el.payments?.filter((p) => {
            const pagoDate = dayjs(p.date, "YYYY-MM-DD").startOf("day");
            return pagoDate.isSameOrBefore(
              dayjs(date, "YYYY-MM-DD").startOf("day")
            );
          }) || [];

        const pagosRealizados = pagosHastaFecha.length;
        const currentPayment = cuotasFiltradas[0] || schedule[0] || {};
        const cuota = currentPayment.cuotaAPagar || 0;
        totalCuotas += cuota;

        const row = [
          el.identity || "",
          `${capitalize(el.name)} ${capitalize(el.lastName)}`,
          el.numberCredit || "",
          `${el.tasa || 0}%`,
          `$${formatCurrency(el.ammount)}`,
          el.month || 0,
          pagosRealizados || el.payments?.length || 0,
          `$${formatCurrencyPDF(cuota)}`,
        ];

        const isEven = i % 2 === 0;
        page.drawRectangle({
          x: tableLeft,
          y: currentY - rowHeight,
          width: tableWidth - 60,
          height: rowHeight,
          color: isEven ? rgb(1, 1, 1) : rgb(0.985, 0.985, 0.985),
          borderColor: rgb(0.8, 0.8, 0.8),
          borderWidth: 0.3,
        });

        x = tableLeft + 4;
        row.forEach((text, j) => {
          page.drawText(String(text), {
            x,
            y: currentY - 12,
            size: 7,
            font,
            color: rgb(0, 0, 0),
          });
          x += colWidths[j];
        });

        currentY -= rowHeight;
      }

      // ======= Totales =======
      page.drawRectangle({
        x: tableLeft,
        y: currentY - rowHeight,
        width: tableWidth - 60,
        height: rowHeight,
        color: rgb(0.9, 0.9, 0.9),
        borderColor: rgb(0.6, 0.6, 0.6),
        borderWidth: 0.5,
      });

      page.drawText("TOTAL CUOTAS", {
        x: tableLeft + tableWidth - 210,
        y: currentY - 12,
        size: 8,
        font: fontBold,
      });

      page.drawText(`$${formatCurrencyPDF(totalCuotas)}`, {
        x: tableLeft + tableWidth - 120,
        y: currentY - 12,
        size: 8,
        font,
      });

      // ======= Mostrar PDF =======
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const newWindow = window.open(url, "_blank");
      setTimeout(() => {
        newWindow.document.title = `Reporte Empresa - ${name}`;
      }, 100);

      setLoading(false);
    } catch (error) {
      console.error("Error generando el PDF:", error);
      setLoading(false);
    }
  };

  return (
    <div>
      <div>
        <label
          htmlFor="date"
          className="block mb-2 text-sm font-medium text-gray-700"
        >
          Fecha:
        </label>
        <input
          type="date"
          id="date"
          name="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
        />
      </div>
      <button
        onClick={() => generarCertificado()}
        className={`!mx-0 rounded-lg py-1 px-2 font-semibold mt-3 bg-richBlack text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        disabled={loading}
        aria-disabled={loading}
      >
        {loading ? "Generando reporte..." : "Descargar Reporte de Cobro"}
      </button>
    </div>
  );
}

export default GenerateReportCompany;
